import api from './api';

/*
 * API client for children — login-less wish-list owners managed by SEVERAL carers.
 *
 * Like a pet, a child has no login: they exist to give a wish a non-user display
 * identity, and the wish still belongs to the carer who added it (`user_id`) while
 * carrying a nullable `child_id`.
 *
 * The one way they differ from pets, and it is the whole point of the feature:
 *
 *   - SEVERAL adults can add to one child's list (the `carers` array), and
 *   - ALL of them see claim/purchase state on it.
 *
 * A pet's caretaker is deliberately blind to purchases — the surprise is kept for the
 * human. A child has no account, so there is nobody to keep a secret from, and two
 * blind carers would just buy the same present twice. That exemption lives in the API
 * (Wish::isHiddenFromViewer); the UI simply renders what it is given.
 *
 * Endpoints (see ../listful-api/docs/KIDS_PLAN.md):
 *   GET    /groups/{group}/children        list (any group member)
 *   POST   /groups/{group}/children        create (creator becomes the first carer)
 *   GET    /children/{child}               detail
 *   PUT    /children/{child}               update (carers only)
 *   DELETE /children/{child}               delete (creator or group admin; wishes cascade)
 *   GET    /children/{child}/wishes        the child's list
 *   POST   /children/{child}/carers        add a carer (carers only)
 *   DELETE /children/{child}/carers/{user} remove a carer (never down to zero)
 *
 * Each function returns the already-unwrapped payload — services/api.js strips the
 * `{ success, data, ... }` envelope.
 */

export async function listGroupChildren(groupId) {
    const res = await api.get(`/groups/${groupId}/children`);
    return res.data;
}

export async function createChild(groupId, payload) {
    const res = await api.post(`/groups/${groupId}/children`, payload);
    return res.data;
}

export async function getChild(childId) {
    const res = await api.get(`/children/${childId}`);
    return res.data;
}

export async function updateChild(childId, payload) {
    const res = await api.put(`/children/${childId}`, payload);
    return res.data;
}

export async function deleteChild(childId) {
    const res = await api.delete(`/children/${childId}`);
    return res.data;
}

export async function listChildWishes(childId) {
    const res = await api.get(`/children/${childId}/wishes`);
    return res.data;
}

export async function addCarer(childId, userId) {
    const res = await api.post(`/children/${childId}/carers`, { user_id: userId });
    return res.data;
}

// The API refuses to remove the last carer (422) — a child whose list nobody can edit
// is the same failure as a group with no admin. Surface that message rather than
// pre-guessing it here, so there is one source of truth for the rule.
export async function removeCarer(childId, userId) {
    const res = await api.delete(`/children/${childId}/carers/${userId}`);
    return res.data;
}

/** Is this user allowed to add to / edit this child's list? */
export function isCarer(child, userId) {
    return Boolean(userId) && (child?.carers || []).some((c) => c.id === userId);
}
